'use client';

import React, { useState, useRef } from 'react';
import { useTranslation } from '@/lib/i18n/useTranslation';

// Default placeholder image for products - a nice box/package icon
const PRODUCT_PLACEHOLDER_IMAGE = "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTI4IiBoZWlnaHQ9IjEyOCIgdmlld0JveD0iMCAwIDEyOCAxMjgiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIxMjgiIGhlaWdodD0iMTI4IiByeD0iMTIiIGZpbGw9IiNmMGY5ZmYiLz4KPHBhdGggZD0iTTY0IDI4TDk2IDQ0VjgwTDY0IDk2TDMyIDgwVjQ0TDY0IDI4WiIgZmlsbD0iI2UwZjJmZSIgc3Ryb2tlPSIjMzg5OWVjIiBzdHJva2Utd2lkdGg9IjMiLz4KPHBhdGggZD0iTTY0IDI4TDY0IDk2IiBzdHJva2U9IiMzODk5ZWMiIHN0cm9rZS13aWR0aD0iMiIgc3Ryb2tlLWRhc2hhcnJheT0iNCw0Ii8+CjxwYXRoIGQ9Ik0zMiA0NEw2NCA2MEw5NiA0NCIgc3Ryb2tlPSIjMzg5OWVjIiBzdHJva2Utd2lkdGg9IjIiLz4KPHBhdGggZD0iTTY0IDYwVjk2IiBzdHJva2U9IiMzODk5ZWMiIHN0cm9rZS13aWR0aD0iMiIvPgo8Y2lyY2xlIGN4PSI2NCIgY3k9IjQ0IiByPSI2IiBmaWxsPSIjM2I4MmY2Ii8+CjxjaXJjbGUgY3g9IjQ4IiBjeT0iNTIiIHI9IjQiIGZpbGw9IiM2MGE1ZmEiLz4KPGNpcmNsZSBjeD0iODAiIGN5PSI1MiIgcj0iNCIgZmlsbD0iIzYwYTVmYSIvPgo8L3N2Zz4=";

interface ImageUploadProps {
  label: string;
  value?: string;
  onChange: (value: string) => void;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
}

export const ImageUpload: React.FC<ImageUploadProps> = ({
  label,
  value,
  onChange,
  required = false,
  disabled = false,
}) => {
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  const handleFileSelect = async (file: File) => {
    if (!file.type.startsWith('image/')) {
      alert(t('inventory.invalidImageFormat'));
      return;
    }

    if (file.size > 5 * 1024 * 1024) { // 5MB limit
      alert(t('inventory.imageTooLarge'));
      return;
    }

    setIsUploading(true);

    try {
      // Convert to base64 for demo (in real app, upload to server)
      const reader = new FileReader();
      reader.onload = (e) => {
        const result = e.target?.result as string;
        onChange(result);
        setIsUploading(false);
      };
      reader.readAsDataURL(file);
    } catch (error) {
      console.error('Error uploading image:', error);
      alert(t('inventory.uploadFailed'));
      setIsUploading(false);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOver(false);

    const file = e.dataTransfer.files[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = () => {
    setIsDragOver(false);
  };

  const removeImage = () => {
    onChange('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className="space-y-2">
      {label && (
        <label className="block text-sm font-medium text-foreground">
          {label}
          {required && <span className="text-danger ml-1">*</span>}
        </label>
      )}

      <div className="space-y-3">
        {/* Image Preview */}
        <div className={`relative inline-block ${disabled ? 'opacity-50' : ''}`}>
          {/* User-uploaded blob/data URLs - next/image needs static or whitelisted hosts. */}
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src={value || PRODUCT_PLACEHOLDER_IMAGE}
            alt="Product"
            className="w-32 h-32 object-cover rounded-lg border border-card-border shadow-sm"
          />
          {!disabled && (
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="absolute inset-0 w-full h-full bg-black/50 opacity-0 hover:opacity-100 transition-opacity flex items-center justify-center rounded-lg"
              title={value ? t('inventory.changeImage') : t('inventory.clickToUpload')}
            >
              <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </button>
          )}
          {value && !disabled && (
            <button
              type="button"
              onClick={removeImage}
              className="absolute -top-2 -right-2 w-6 h-6 bg-danger text-white rounded-full flex items-center justify-center hover:bg-danger-hover transition-colors shadow-lg"
              title={t('inventory.removeImage')}
            >
              <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>

        {/* Upload Area */}
        {!disabled && (
        <div
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onClick={() => fileInputRef.current?.click()}
          className={`border-2 border-dashed rounded-lg p-4 text-center cursor-pointer transition-all duration-200 ${
            isDragOver
              ? 'border-primary bg-primary/5 scale-105'
              : 'border-card-border hover:border-primary hover:bg-primary/5'
          } ${isUploading ? 'pointer-events-none opacity-50' : ''}`}
        >
          <div className="space-y-2">
            {isUploading ? (
              <>
                <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full mx-auto"></div>
                <p className="text-sm text-foreground-muted font-medium">{t('inventory.uploading')}</p>
              </>
            ) : (
              <>
                <p className="text-sm font-medium text-foreground">
                  {t('inventory.clickToUpload')} {t('inventory.or')} {t('inventory.dragAndDrop')}
                </p>
                <p className="text-xs text-foreground-muted">
                  PNG, JPG, GIF {t('inventory.upTo')} 5MB
                </p>
              </>
            )}
          </div>
        </div>
        )}
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileChange}
        className="hidden"
      />
    </div>
  );
};

